import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import GlobalStyles from '@mui/material/GlobalStyles';
import { ReactNode, forwardRef, useImperativeHandle, useRef } from 'react';
import Header from './Header';
import PageSidebar from './PageSidebar';

interface Props {
  header?: ReactNode;
  content: ReactNode;
  scroll?: string;
  className?: string;
  closeIconColor?: string;
  rightSidebarContent?: ReactNode;
  rightSidebarVariant?: string;
  rightSidebarOpen?: boolean;
  rightSidebarWidth?: number;
  leftSidebarContent?: ReactNode;
  leftSidebarVariant?: string;
  leftSidebarOpen?: boolean;
  leftSidebarWidth?: number;
  drawerVariant?: 'temporary' | 'persistent' | 'permanent';
  rightSidebarOnClose?: () => void;
  leftSidebarOnClose?: () => void;
}

const Root = styled('div')<any>(({ theme, scroll, rightsidebarwidth, leftsidebarwidth, ...props }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  minHeight: '100%',
  position: 'relative',
  flex: '1 1 auto',
  width: '100%',
  height: 'auto',
  backgroundColor: theme.palette.background.default,

  '&.FusePageSimple-scroll-content': {
    height: '100%',
  },

  '& .FusePageSimple-wrapper': {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    zIndex: 2,
    minWidth: 0,
    height: '100%',
    backgroundColor: theme.palette.background.default,

    ...(scroll === 'content' && {
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      overflow: 'hidden',
    }),
  },

  '& .FusePageSimple-header': {
    display: 'flex',
    flex: '0 0 auto',
    backgroundSize: 'cover',
  },

  '& .FusePageSimple-topBg': {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 120,
    pointerEvents: 'none',
  },

  '& .FusePageSimple-contentWrapper': {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: '1 1 auto',
    overflow: 'hidden',
    zIndex: 9999,
  },

  '& .FusePageSimple-toolbar': {
    height: 64,
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
  }, 

  '& .FusePageSimple-content': {
    display: 'flex',
    flex: '1 1 auto',
    alignItems: 'start',
    minHeight: 0,
    overflowY: 'auto',
  },

  '& .FusePageSimple-sidebarWrapper': {
    overflow: 'hidden',
    backgroundColor: 'transparent',
    position: 'absolute',
    '&.permanent': {
      [theme.breakpoints.up('lg')]: {
        position: 'relative',
        marginLeft: 0,
        marginRight: 0,
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        '&.closed': {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),

          '&.FusePageSimple-leftSidebar': {
            marginLeft: -leftsidebarwidth,
          },
          '&.FusePageSimple-rightSidebar': {
            marginRight: -rightsidebarwidth,
          },
        },
      },
    },
  },

  '& .FusePageSimple-sidebar': {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,

    '&.permanent': {
      [theme.breakpoints.up('lg')]: {
        position: 'relative',
      },
    },
    maxWidth: '100%',
    height: '100%',
  },

  '& .FusePageSimple-leftSidebar': {
    width: leftsidebarwidth,

    [theme.breakpoints.up('lg')]: {
      borderRight: `1px solid ${theme.palette.divider}`,
      borderLeft: 0,
    },
  },

  '& .FusePageSimple-rightSidebar': {
    width: rightsidebarwidth,

    [theme.breakpoints.up('lg')]: {
      borderLeft: `1px solid ${theme.palette.divider}`,
      borderRight: 0,
    },
  },

  '& .FusePageSimple-sidebarHeader': {
    height: 120,
    minHeight: 120,
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },

  '& .FusePageSimple-sidebarHeaderInnerSidebar': {
    backgroundColor: 'transparent',
    color: 'inherit',
    height: 'auto',
    minHeight: 'auto',
  },

  '& .FusePageSimple-sidebarContent': {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
  },

  '& .FusePageSimple-backdrop': {
    position: 'absolute',
  },
}));

const Content = styled('div')(() => ({
  overscrollBehavior: 'contain',
  minHeight: '100%',
}));

const Page = forwardRef((props: Props, ref) => {
  const {
    className = '',
    scroll = 'page',
    header,
    content,
    closeIconColor = '#000',
    rightSidebarContent,
    rightSidebarVariant = 'permanent',
    rightSidebarOpen = false,
    rightSidebarWidth = 350,
    rightSidebarOnClose = () => {},
    leftSidebarContent,
    leftSidebarVariant = 'permanent',
    leftSidebarOpen = false,
    leftSidebarWidth = 350,
    leftSidebarOnClose = () => {},
    drawerVariant = 'temporary'
  } = props;

  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const rootRef = useRef(null);

  useImperativeHandle(ref, () => ({
    rootRef,
    toggleLeftSidebar: (val: any) => {
      (leftSidebarRef.current as any).toggleSidebar(val);
    },
    toggleRightSidebar: (val: any) => {
      (rightSidebarRef.current as any).toggleSidebar(val);
    },
  }));

  return (
    <>
      <GlobalStyles
        styles={() => ({
          ...(scroll !== 'page' && {
            '#fuse-toolbar': {
              position: 'static',
            },
          }),
          ...(scroll === 'page' && {
            '#fuse-toolbar': {
              position: 'sticky',
              top: 0,
            },
          }),
        })}
      />
      <Root
        className={clsx(
          'FusePageSimple-root',
          `FusePageSimple-scroll-${props.scroll}`,
          className
        )}
        ref={rootRef}
        scroll={scroll}
        leftsidebarwidth={leftSidebarWidth}
        rightsidebarwidth={rightSidebarWidth}
      >
        <div className="flex flex-auto flex-col z-10 h-full">
          <div className="FusePageSimple-wrapper">
            {leftSidebarContent && (
              <PageSidebar
                position='left'
                content={leftSidebarContent}
                variant={leftSidebarVariant}
                drawerVariant={drawerVariant}
                open={leftSidebarOpen}
                ref={leftSidebarRef}
                rootRef={rootRef}
                closeIconColor={closeIconColor}
                sidebarWidth={leftSidebarWidth}
                onClose={leftSidebarOnClose}
              />
            )}

            <div className="FusePageSimple-contentWrapper">
              {header && <Header content={header} />}
              {content && (
                <Content className="FusePageSimple-content container">
                  {content}
                </Content>
              )}
            </div>

            {rightSidebarContent && (
              <PageSidebar
                position='right'
                content={rightSidebarContent}
                variant={rightSidebarVariant}
                drawerVariant={drawerVariant}
                open={rightSidebarOpen}
                ref={rightSidebarRef}
                rootRef={rootRef}
                closeIconColor={closeIconColor}
                sidebarWidth={rightSidebarWidth}
                onClose={rightSidebarOnClose}
              />
            )}
          </div>
        </div>
      </Root>
    </>
  );
});

export default Page;
